import React, { useEffect, useState } from 'react';
import {
  Alert,
  Snackbar,
  CircularProgress,
  AlertColor,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Chip,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import {
  Plus,
  Pencil,
  Trash2,
  Tag,
  Users,
  BarChart2,
  ChevronRight,
  X,
  Upload,
  CalendarDays,
  DollarSign,
  TrendingUp,
  ArrowLeft,
  CheckCircle2,
  Clock,
  AlertCircle,
  Trophy,
  Filter,
  Search,
  ChevronLeft,
} from 'lucide-react';
import api from '../utils/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type ProgressStatus = 'completed' | 'in_progress' | 'not_started';
type FilterTab = 'all' | 'completed' | 'in_progress' | 'top';

interface Offer {
  _id: string;
  name: string;
  image: string;
  description?: string;
  businessVolumeRequired?: number;
  teamLevels?: number[];
  imageTransform?: { scale: number; offsetY: number };
  totalDirectInvestmentRequired: number; // legacy
  selfInvestmentRequired: number;
  startDate: string;
  endDate: string;
  createdAt: string;
}

interface EligibleUser {
  name: string;
  email: string;
  number?: string;
  teamInvestment: number;
  selfInvestment: number;
  teamProgress: number;
  selfProgress: number | null;
  status: ProgressStatus;
}

interface ProgressUser {
  email: string;
  name: string;
  number?: string;
  teamInvestment: number;
  selfInvestment: number;
  teamProgress: number;
  selfProgress: number | null;
  overallProgress: number;
  status: ProgressStatus;
  businessVolumeByLevel?: { level: number; amount: number }[];
}

interface TeamActivity {
  memberName: string;
  memberEmail: string;
  amount: number;
  date: string;
  subscription: string;
}

interface UserOfferDetail {
  user: { name: string; email: string; number?: string };
  offer: {
    name: string;
    startDate: string;
    endDate: string;
    teamInvestmentRequired: number;
    businessVolumeRequired?: number;
    teamLevels?: number[];
    selfInvestmentRequired: number;
  };
  teamInvestment: number;
  selfInvestment: number;
  teamProgress: number;
  selfProgress: number | null;
  overallProgress: number;
  status: ProgressStatus;
  businessVolumeByLevel?: { level: number; amount: number }[];
  teamActivity: TeamActivity[];
  selfInvestments: { amount: number; date: string; subscription: string }[];
}

interface OfferForm {
  name: string;
  businessVolumeRequired: string;
  description: string;
  teamLevels: number[];
  selfRequired: string;
  startDate: string;
  endDate: string;
  imageUrl: string;
  imagePreview: string;
  imageScale: number;
  imageOffsetY: number;
}

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ProgressStatus, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  completed: {
    label: 'Completed',
    color: '#10b981',
    bg: 'bg-emerald-50',
    icon: <CheckCircle2 size={13} className="text-emerald-500" />,
  },
  in_progress: {
    label: 'In Progress',
    color: '#f59e0b',
    bg: 'bg-amber-50',
    icon: <Clock size={13} className="text-amber-500" />,
  },
  not_started: {
    label: 'Not Started',
    color: '#ef4444',
    bg: 'bg-red-50',
    icon: <AlertCircle size={13} className="text-red-400" />,
  },
};

const StatusBadge: React.FC<{ status: ProgressStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg}`}
      style={{ color: cfg.color }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const ProgressBar: React.FC<{
  value: number;
  color?: string;
  height?: number;
  showOverachiever?: boolean;
}> = ({ value, color = '#523680', height = 7, showOverachiever = false }) => (
  <div className="relative">
    <LinearProgress
      variant="determinate"
      value={Math.min(100, value)}
      sx={{
        height,
        borderRadius: height / 2,
        backgroundColor: 'rgba(0,0,0,0.06)',
        '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: height / 2 },
      }}
    />
    {showOverachiever && value > 100 && (
      <span className="absolute -top-0.5 right-0 text-[9px] font-black text-amber-500 bg-amber-50 px-1 py-0.5 rounded-full border border-amber-200">
        🏆 {value.toFixed(0)}%
      </span>
    )}
  </div>
);

// ─── Image Upload Zone ────────────────────────────────────────────────────────

const ImageUploadZone: React.FC<{
  preview: string;
  uploading: boolean;
  onDrop: (files: File[]) => void;
}> = ({ preview, uploading, onDrop }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/png': [], 'image/webp': [] },
    maxFiles: 1,
    disabled: uploading,
  });

  return (
    <div
      {...getRootProps()}
      className={`relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-all duration-200 overflow-hidden
        ${isDragActive ? 'border-[#523680] bg-[#523680]/5' : 'border-gray-200 hover:border-[#523680]/50 hover:bg-gray-50'}
        ${uploading ? 'opacity-60 cursor-not-allowed' : ''}`}
      style={{ minHeight: 148 }}
    >
      <input {...getInputProps()} />
      {preview ? (
        <>
          <img src={preview} alt="preview" className="w-full h-36 object-cover" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
            <span className="text-white text-xs font-bold bg-black/50 px-3 py-1 rounded-full">
              Change Image
            </span>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Upload size={26} className="text-gray-300" />
          <p className="text-sm font-medium text-gray-400">Drop image here</p>
          <p className="text-xs text-gray-300">JPEG / PNG / WEBP</p>
        </div>
      )}
      {uploading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
          <CircularProgress size={22} sx={{ color: '#523680' }} />
        </div>
      )}
    </div>
  );
};

// ─── Offer Form Modal ─────────────────────────────────────────────────────────

const OfferFormModal: React.FC<{
  open: boolean;
  editing: Offer | null;
  onClose: () => void;
  onSaved: () => void;
  onSnack: (msg: string, sev: AlertColor) => void;
}> = ({ open, editing, onClose, onSaved, onSnack }) => {
  const blank: OfferForm = {
    name: '',
    businessVolumeRequired: '',
    description: '',
    teamLevels: [1],
    selfRequired: '',
    startDate: '',
    endDate: '',
    imageUrl: '',
    imagePreview: '',
    imageScale: 1,
    imageOffsetY: 0,
  };

  const [form, setForm] = useState<OfferForm>(blank);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartY, setDragStartY] = useState<number | null>(null);
  const [dragStartOffsetY, setDragStartOffsetY] = useState<number>(0);

  useEffect(() => {
    if (open) {
      setForm(
        editing
          ? {
              name: editing.name,
              businessVolumeRequired: String(editing.businessVolumeRequired ?? editing.totalDirectInvestmentRequired ?? 0),
              description: editing.description || '',
              teamLevels: editing.teamLevels?.length ? editing.teamLevels : [1],
              selfRequired: editing.selfInvestmentRequired > 0 ? String(editing.selfInvestmentRequired) : '',
              startDate: editing.startDate.slice(0, 10),
              endDate: editing.endDate.slice(0, 10),
              imageUrl: editing.image,
              imagePreview: editing.image,
              imageScale: editing.imageTransform?.scale ?? 1,
              imageOffsetY: editing.imageTransform?.offsetY ?? 0,
            }
          : blank
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, open]);

  const handleImageDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setForm((f) => ({ ...f, imagePreview: preview }));
    setUploadingImg(true);
    try {
      const { data } = await api.post('/api/offers/getSignedUrl', { fileType: file.type });
      const { uploadUrl, publicUrl } = data.data;
      const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: {} });
      if (!res.ok) throw new Error('S3 upload failed');
      setForm((f) => ({ ...f, imageUrl: publicUrl }));
      onSnack('Image uploaded', 'success');
    } catch {
      onSnack('Image upload failed', 'error');
      setForm((f) => ({ ...f, imagePreview: editing?.image || '' }));
    } finally {
      setUploadingImg(false);
      URL.revokeObjectURL(preview);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.imageUrl) { onSnack('Please upload an offer image', 'error'); return; }
    if (form.endDate < form.startDate) { onSnack('End date must be after start date', 'error'); return; }

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description || '',
        image: form.imageUrl,
        businessVolumeRequired: Number(form.businessVolumeRequired) || 0,
        teamLevels: form.teamLevels,
        imageTransform: { scale: form.imageScale, offsetY: form.imageOffsetY },
        selfInvestmentRequired: form.selfRequired ? Number(form.selfRequired) : 0,
        startDate: form.startDate,
        endDate: form.endDate,
      };
      if (editing) {
        await api.put(`/api/offers/${editing._id}`, payload);
        onSnack('Offer updated', 'success');
      } else {
        await api.post('/api/offers', payload);
        onSnack('Offer created', 'success');
      }
      onSaved();
      onClose();
    } catch (err: any) {
      onSnack(err.response?.data?.message || 'Failed to save offer', 'error');
    } finally {
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: keyof OfferForm,
    type: string = 'text',
    placeholder: string = '',
    required = true,
    hint?: string
  ) => (
    <div>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
        {label}
        {!required && (
          <span className="text-[9px] font-normal text-gray-300 normal-case tracking-normal border border-gray-200 px-1 rounded">
            optional
          </span>
        )}
      </label>
      <input
        type={type}
        required={required}
        value={form[key] as string}
        min={type === 'number' ? 0 : undefined}
        step={type === 'number' ? 'any' : undefined}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/25 focus:border-[#523680] transition-colors"
        placeholder={placeholder}
      />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <div className="flex items-center justify-between">
          <span className="text-lg font-black text-gray-900">
            {editing ? 'Edit Offer' : 'Create New Offer'}
          </span>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 2 }}>
          <div className="space-y-4">
            <div
              onMouseDown={(e) => {
                if (!form.imagePreview) return;
                setIsDragging(true);
                setDragStartY(e.clientY);
                setDragStartOffsetY(form.imageOffsetY);
              }}
              onMouseMove={(e) => {
                if (!isDragging || dragStartY === null) return;
                const delta = e.clientY - dragStartY;
                setForm((f) => ({
                  ...f,
                  imageOffsetY: Math.min(Math.max(dragStartOffsetY + delta, -300), 300),
                }));
              }}
              onMouseUp={() => {
                setIsDragging(false);
                setDragStartY(null);
              }}
              onMouseLeave={() => {
                setIsDragging(false);
                setDragStartY(null);
              }}
              onTouchStart={(e) => {
                if (!form.imagePreview) return;
                const touch = e.touches[0];
                setIsDragging(true);
                setDragStartY(touch.clientY);
                setDragStartOffsetY(form.imageOffsetY);
              }}
              onTouchMove={(e) => {
                if (!isDragging || dragStartY === null) return;
                const touch = e.touches[0];
                const delta = touch.clientY - dragStartY;
                setForm((f) => ({
                  ...f,
                  imageOffsetY: Math.min(Math.max(dragStartOffsetY + delta, -300), 300),
                }));
              }}
              onTouchEnd={() => {
                setIsDragging(false);
                setDragStartY(null);
              }}
            >
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Offer Image *
              </label>
              <div className="space-y-2">
                <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                  <div className="h-36 w-full overflow-hidden">
                    {form.imagePreview ? (
                      <img
                        src={form.imagePreview}
                        alt="preview"
                        className="w-full h-full object-cover"
                        style={{
                          transform: `translateY(${form.imageOffsetY}px) scale(${form.imageScale})`,
                          transformOrigin: 'center',
                          cursor: isDragging ? 'grabbing' : 'grab',
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 font-semibold">
                        Upload an image to preview adjustments
                      </div>
                    )}
                  </div>
                  <div className="p-3 bg-white border-t border-gray-100">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-[220px]">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Zoom</span>
                        <input
                          type="range"
                          min={0.5}
                          max={3}
                          step={0.05}
                          value={form.imageScale}
                          onChange={(e) => setForm((f) => ({ ...f, imageScale: Number(e.target.value) }))}
                          disabled={!form.imagePreview}
                          className="w-full accent-[#523680]"
                        />
                        <span className="text-[10px] font-black text-gray-500 w-10 text-right">
                          {Math.round(form.imageScale * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <ImageUploadZone preview={form.imagePreview} uploading={uploadingImg} onDrop={handleImageDrop} />
              </div>
            </div>

            {field('Offer Name', 'name', 'text', 'e.g. Summer Investment Bonus')}

            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Description
                <span className="text-[9px] font-normal text-gray-300 normal-case tracking-normal border border-gray-200 px-1 rounded">
                  optional
                </span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/25 focus:border-[#523680] transition-colors"
                placeholder="Short description shown with this offer (optional)"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field(
                'Business Volume Required',
                'businessVolumeRequired',
                'number',
                '0',
                true,
                'Calculated from team investments within offer period'
              )}
              {field(
                'Self Investment Required',
                'selfRequired',
                'number',
                '0 (leave blank to skip)',
                false,
                'Leave empty to exclude from eligibility check'
              )}
            </div>

            <div>
              <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Count Team Volume Levels
              </label>
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4, 5].map((lvl) => {
                  const selected = form.teamLevels.includes(lvl);
                  return (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setForm((f) => {
                        const next = selected ? f.teamLevels.filter((n) => n !== lvl) : [...f.teamLevels, lvl];
                        const uniq = Array.from(new Set(next)).sort((a, b) => a - b);
                        return { ...f, teamLevels: uniq.length ? uniq : [1] };
                      })}
                      className={`px-3 py-1.5 rounded-full text-xs font-black border transition-colors ${
                        selected
                          ? 'bg-[#523680] text-white border-[#523680]'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      Level {lvl}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                For now calculations still use direct (Level 1). Selecting more levels stores config for upcoming rollout.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field('Start Date', 'startDate', 'date', '', true)}
              {field('End Date', 'endDate', 'date', '', true)}
            </div>
          </div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || uploadingImg}
            className="px-6 py-2 rounded-xl bg-[#523680] text-white text-sm font-bold hover:bg-[#523680]/90 transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <CircularProgress size={13} sx={{ color: 'white' }} />}
            {editing ? 'Save Changes' : 'Create Offer'}
          </button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

// ─── Eligible Users Modal ─────────────────────────────────────────────────────

const EligibleUsersModal: React.FC<{
  open: boolean;
  offer: Offer | null;
  onClose: () => void;
}> = ({ open, offer, onClose }) => {
  const [users, setUsers] = useState<EligibleUser[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !offer) return;
    setLoading(true);
    api
      .get(`/api/offers/${offer._id}/eligible-users`)
      .then((r) => setUsers(r.data.data || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [open, offer]);

  const hasSelf = (offer?.selfInvestmentRequired ?? 0) > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-black text-gray-900">Eligible Users</p>
            {offer && (
              <p className="text-xs text-gray-400 font-normal mt-0.5">
                {offer.name} &bull; Team ≥ ₹{offer.totalDirectInvestmentRequired.toLocaleString()}
                {hasSelf && ` & Self ≥ ₹${offer.selfInvestmentRequired.toLocaleString()}`}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <CircularProgress sx={{ color: '#523680' }} />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <Users size={38} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No eligible users found</p>
            <p className="text-sm mt-1">
              No users have met the eligibility criteria for this offer yet.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {users.length} eligible user{users.length !== 1 ? 's' : ''}
            </p>
            {users.map((u) => (
              <div
                key={u.email}
                className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-emerald-200 hover:bg-emerald-50/40 transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-sm">
                    {u.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{u.name}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-gray-400">Team</p>
                    <p className="text-sm font-bold text-emerald-600">
                      ₹{u.teamInvestment.toLocaleString()}
                    </p>
                  </div>
                  {hasSelf && (
                    <div className="text-right">
                      <p className="text-xs text-gray-400">Self</p>
                      <p className="text-sm font-bold text-blue-600">
                        ₹{u.selfInvestment.toLocaleString()}
                      </p>
                    </div>
                  )}
                  <StatusBadge status="completed" />
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── User Detail Modal ────────────────────────────────────────────────────────

const UserDetailModal: React.FC<{
  open: boolean;
  detail: UserOfferDetail | null;
  loading: boolean;
  onClose: () => void;
}> = ({ open, detail, loading, onClose }) => {
  if (!open) return null;

  const teamReq = detail?.offer.teamInvestmentRequired ?? 0;
  const bvStored = detail?.offer.businessVolumeRequired ?? 0;
  /** Same as App backend effectiveBVRequired */
  const teamBvTarget = bvStored > 0 ? bvStored : teamReq;
  const selfReq = detail?.offer.selfInvestmentRequired ?? 0;
  const hasSelf = selfReq > 0;

  const barColor = (pct: number) =>
    pct >= 100 ? '#10b981' : pct >= 60 ? '#523680' : pct >= 30 ? '#f59e0b' : '#ef4444';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-black text-gray-900">
              {detail?.user?.name || 'User Detail'}
            </p>
            <p className="text-xs text-gray-400 font-normal">{detail?.offer?.name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <div className="flex justify-center py-12">
            <CircularProgress sx={{ color: '#523680' }} />
          </div>
        ) : !detail ? null : (
          <div className="space-y-5">

            {/* Status + basic info */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-11 h-11 rounded-full bg-[#523680]/10 flex items-center justify-center text-[#523680] font-black text-base">
                {detail.user?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-800">{detail.user?.name}</p>
                <p className="text-xs text-gray-400">{detail.user?.email}</p>
              </div>
              <StatusBadge status={detail.status} />
              {detail.overallProgress > 100 && (
                <span className="text-xs font-black text-amber-500 flex items-center gap-1">
                  <Trophy size={13} /> Overachiever
                </span>
              )}
            </div>

            {/* Progress bars */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-600">Team Investment Progress</span>
                  <span className="text-xs font-black" style={{ color: barColor(detail.teamProgress) }}>
                    {detail.teamProgress.toFixed(1)}%
                    {detail.teamProgress > 100 && <span className="ml-1 text-amber-500">🏆</span>}
                  </span>
                </div>
                <ProgressBar
                  value={detail.teamProgress}
                  color={barColor(detail.teamProgress)}
                  showOverachiever={detail.teamProgress > 100}
                />
                <div className="flex justify-between mt-0.5">
                  <span className="text-xs text-gray-400">
                    ₹{detail.teamInvestment.toLocaleString()} invested
                  </span>
                  {teamBvTarget > 0 && (
                    <span className="text-xs text-gray-400">
                      Required: ₹{teamBvTarget.toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {detail.businessVolumeByLevel && detail.businessVolumeByLevel.length > 0 && (
                <div className="rounded-xl border border-gray-100 bg-gray-50/80 p-3">
                  <p className="text-xs font-semibold text-gray-600 mb-2">
                    Business volume by level (offer window)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {detail.businessVolumeByLevel.map((row) => (
                      <div
                        key={row.level}
                        className="px-2.5 py-1 rounded-lg bg-white border border-gray-100 text-xs"
                      >
                        <span className="text-gray-500">L{row.level}:</span>{' '}
                        <span className="font-bold text-gray-800">₹{row.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {hasSelf && detail.selfProgress !== null && (
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-600">Self Investment Progress</span>
                    <span className="text-xs font-black" style={{ color: barColor(detail.selfProgress) }}>
                      {detail.selfProgress.toFixed(1)}%
                      {detail.selfProgress > 100 && <span className="ml-1 text-amber-500">🏆</span>}
                    </span>
                  </div>
                  <ProgressBar
                    value={detail.selfProgress}
                    color={barColor(detail.selfProgress)}
                    showOverachiever={detail.selfProgress > 100}
                  />
                  <div className="flex justify-between mt-0.5">
                    <span className="text-xs text-gray-400">
                      ₹{detail.selfInvestment.toLocaleString()} invested
                    </span>
                    <span className="text-xs text-gray-400">
                      Required: ₹{selfReq.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Investment breakdown cards */}
            <div className={`grid gap-3 ${hasSelf ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                <p className="text-xs font-semibold text-emerald-600 mb-0.5">Total Team Investment</p>
                <p className="text-2xl font-black text-emerald-700">
                  ₹{detail.teamInvestment.toLocaleString()}
                </p>
                <p className="text-xs text-emerald-500 mt-0.5">within offer period</p>
              </div>
              {hasSelf && (
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-600 mb-0.5">Self Investment</p>
                  <p className="text-2xl font-black text-blue-700">
                    ₹{detail.selfInvestment.toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-500 mt-0.5">within offer period</p>
                </div>
              )}
            </div>

            {/* Team Activity */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-bold text-gray-700">Team Activity</p>
                <Chip
                  label={detail.teamActivity.length}
                  size="small"
                  sx={{ bgcolor: '#523680', color: 'white', fontSize: 11, height: 20, fontWeight: 700 }}
                />
                <span className="text-xs text-gray-400">transactions within offer period</span>
              </div>
              {detail.teamActivity.length === 0 ? (
                <p className="text-xs text-gray-400 italic py-2">
                  No team investments recorded in the offer window.
                </p>
              ) : (
                <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                  {detail.teamActivity.map((act, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-gray-50 border border-gray-100"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">
                          {act.memberName}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{act.memberEmail}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="text-sm font-bold text-emerald-600">
                          +₹{act.amount.toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(act.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Self investments */}
            {hasSelf && detail.selfInvestments.length > 0 && (
              <div>
                <p className="text-sm font-bold text-gray-700 mb-2">Self Investment Records</p>
                <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                  {detail.selfInvestments.map((inv, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-2.5 rounded-xl bg-blue-50 border border-blue-100"
                    >
                      <span className="text-xs text-gray-500">{inv.subscription}</span>
                      <span className="text-xs text-gray-400">
                        {new Date(inv.date).toLocaleDateString()}
                      </span>
                      <span className="text-sm font-bold text-blue-700">
                        ₹{inv.amount.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Progress Panel ───────────────────────────────────────────────────────────

const ProgressPanel: React.FC<{ offer: Offer; onBack: () => void }> = ({ offer, onBack }) => {
  const [rows, setRows] = useState<ProgressUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({
    all: 0,
    completed: 0,
    in_progress: 0,
    top: 0,
  });
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState<UserOfferDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const hasSelf = offer.selfInvestmentRequired > 0;

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, filter, limit, offer._id]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      search,
      status: filter,
    });
    api
      .get(`/api/offers/${offer._id}/progress?${params.toString()}`)
      .then((r) => {
        setRows(r.data.data || []);
        setTotal(typeof r.data.total === 'number' ? r.data.total : (r.data.count ?? 0));
        setTotalPages(typeof r.data.totalPages === 'number' ? r.data.totalPages : 1);
        if (r.data.counts) {
          setCounts(r.data.counts);
        }
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      })
      .finally(() => setLoading(false));
  }, [offer._id, page, limit, search, filter]);

  const openDetail = async (email: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const { data } = await api.get(
        `/api/offers/${offer._id}/users/${encodeURIComponent(email)}`
      );
      setSelectedDetail(data.data);
    } catch {
      setSelectedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const barColor = (pct: number) =>
    pct >= 100 ? '#10b981' : pct >= 60 ? '#3b82f6' : pct >= 30 ? '#f59e0b' : '#ef4444';

  const TABS: { key: FilterTab; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'All', icon: <Filter size={12} /> },
    { key: 'completed', label: 'Completed', icon: <CheckCircle2 size={12} /> },
    { key: 'in_progress', label: 'In Progress', icon: <Clock size={12} /> },
    { key: 'top', label: 'Top 10', icon: <Trophy size={12} /> },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#523680] to-[#7a52be] p-5 text-white">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs mb-3 transition-colors"
        >
          <ArrowLeft size={14} /> Back to offers
        </button>
        <div className="flex items-center gap-3">
          <img
            src={offer.image}
            alt={offer.name}
            className="w-12 h-12 rounded-xl object-cover border-2 border-white/20 flex-shrink-0"
          />
          <div>
            <h2 className="text-xl font-black leading-tight">{offer.name}</h2>
            <p className="text-white/60 text-xs">
              {new Date(offer.startDate).toLocaleDateString()} →{' '}
              {new Date(offer.endDate).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-4 divide-x divide-gray-100 border-b border-gray-100">
        {[
          { label: 'Team Req.', value: `₹${offer.totalDirectInvestmentRequired.toLocaleString()}`, icon: <TrendingUp size={14} className="text-emerald-500" /> },
          { label: 'Self Req.', value: hasSelf ? `₹${offer.selfInvestmentRequired.toLocaleString()}` : '—', icon: <DollarSign size={14} className="text-blue-500" /> },
          { label: 'Completed', value: loading ? '—' : counts.completed, icon: <CheckCircle2 size={14} className="text-emerald-500" /> },
          { label: 'In Progress', value: loading ? '—' : counts.in_progress, icon: <Clock size={14} className="text-amber-500" /> },
        ].map((s) => (
          <div key={s.label} className="px-4 py-3 text-center bg-white">
            <div className="flex justify-center mb-0.5">{s.icon}</div>
            <p className="text-base font-black text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-400">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 p-3 border-b border-gray-100 bg-gray-50/50">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              filter === t.key
                ? 'bg-[#523680] text-white shadow-sm'
                : 'text-gray-500 hover:bg-white hover:text-gray-800'
            }`}
          >
            {t.icon}
            {t.label}
            <span
              className={`px-1.5 py-0 rounded-full text-[10px] font-black ${
                filter === t.key ? 'bg-white/20 text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {counts[t.key]}
            </span>
          </button>
        ))}
      </div>

      {/* Search + page size */}
      <div className="flex flex-col sm:flex-row gap-2 px-4 pt-3 pb-2 border-b border-gray-100 bg-white">
        <div className="relative flex-1 min-w-0">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            size={16}
          />
          <input
            type="search"
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#523680]/30 focus:border-[#523680]"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 whitespace-nowrap">
          <span>Per page</span>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-2 py-2 text-sm font-semibold text-gray-800 bg-white"
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Progress List */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <CircularProgress sx={{ color: '#523680' }} />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-14 text-gray-400">
            <BarChart2 size={38} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No users in this category</p>
            <p className="text-sm mt-1">
              {search
                ? 'No users match your search.'
                : filter === 'completed'
                  ? 'No users have completed the offer yet.'
                  : 'No data available.'}
            </p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {rows.map((u, idx) => (
              <div
                key={u.email}
                onClick={() => openDetail(u.email)}
                className="group flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-[#523680]/25 hover:bg-[#523680]/3 cursor-pointer transition-all"
              >
                {/* Rank */}
                <span className="w-8 text-xs font-black text-gray-300 text-center flex-shrink-0 tabular-nums">
                  #{(page - 1) * limit + idx + 1}
                </span>

                {/* Avatar */}
                <div
                  className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-black flex-shrink-0"
                  style={{ backgroundColor: STATUS_CONFIG[u.status].color }}
                >
                  {u.name?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <p className="text-sm font-semibold text-gray-800 truncate">{u.name}</p>
                    <StatusBadge status={u.status} />
                    {u.overallProgress > 100 && (
                      <span className="text-[10px] font-black text-amber-500">🏆</span>
                    )}
                  </div>

                  {/* Team bar */}
                  <div className="mb-1">
                    <div className="flex justify-between mb-0.5">
                      <span className="text-[10px] text-gray-400">Team</span>
                      <span
                        className="text-[10px] font-bold"
                        style={{ color: barColor(u.teamProgress) }}
                      >
                        {u.teamProgress.toFixed(1)}%
                      </span>
                    </div>
                    <ProgressBar value={u.teamProgress} color={barColor(u.teamProgress)} height={5} />
                  </div>

                  {/* Self bar (only if required) */}
                  {hasSelf && u.selfProgress !== null && (
                    <div>
                      <div className="flex justify-between mb-0.5">
                        <span className="text-[10px] text-gray-400">Self</span>
                        <span
                          className="text-[10px] font-bold"
                          style={{ color: barColor(u.selfProgress) }}
                        >
                          {u.selfProgress.toFixed(1)}%
                        </span>
                      </div>
                      <ProgressBar value={u.selfProgress} color={barColor(u.selfProgress)} height={5} />
                    </div>
                  )}

                  <div className="flex gap-3 mt-1">
                    <span className="text-[10px] text-gray-400">
                      Team: ₹{u.teamInvestment.toLocaleString()}
                    </span>
                    {hasSelf && (
                      <span className="text-[10px] text-gray-400">
                        Self: ₹{u.selfInvestment.toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                <ChevronRight
                  size={15}
                  className="text-gray-200 group-hover:text-[#523680] transition-colors flex-shrink-0"
                />
              </div>
            ))}
          </div>
        )}

        {!loading && rows.length > 0 && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Showing{' '}
              <span className="font-semibold text-gray-700">
                {(page - 1) * limit + 1}–{Math.min(page * limit, total)}
              </span>{' '}
              of <span className="font-semibold text-gray-700">{total}</span>
              {filter === 'top' && (
                <span className="text-gray-400"> · Top {Math.min(10, counts.all)} overall</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none"
              >
                <ChevronLeft size={16} />
                Prev
              </button>
              <span className="text-xs text-gray-600 tabular-nums px-2">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:pointer-events-none"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && filter === 'top' && totalPages <= 1 && (
          <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100 text-center">
            Top {Math.min(10, total)} by overall progress
            {search ? ' (matching search)' : ''}
          </p>
        )}
      </div>

      <UserDetailModal
        open={detailOpen}
        detail={selectedDetail}
        loading={detailLoading}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
};

// ─── Offer Card ───────────────────────────────────────────────────────────────

const OfferCard: React.FC<{
  offer: Offer;
  onEdit: (o: Offer) => void;
  onDelete: (id: string) => void;
  onEligible: (o: Offer) => void;
  onProgress: (o: Offer) => void;
}> = ({ offer, onEdit, onDelete, onEligible, onProgress }) => {
  const now = new Date();
  const start = new Date(offer.startDate);
  const end = new Date(offer.endDate);
  const isActive = now >= start && now <= end;
  const isExpired = now > end;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col">
      <div className="relative h-44 bg-gray-50 overflow-hidden">
        <img
          src={offer.image}
          alt={offer.name}
          className="w-full h-full object-cover"
          style={{
            transform: `translateY(${offer.imageTransform?.offsetY ?? 0}px) scale(${offer.imageTransform?.scale ?? 1})`,
            transformOrigin: 'center',
          }}
        />
        <div className="absolute top-3 right-3">
          <span
            className={`text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm ${
              isActive ? 'bg-emerald-500 text-white' : isExpired ? 'bg-gray-400 text-white' : 'bg-amber-400 text-white'
            }`}
          >
            {isActive ? '● Active' : isExpired ? 'Expired' : '◌ Upcoming'}
          </span>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-1">
        <h3 className="text-base font-black text-gray-900 mb-3 leading-snug">{offer.name}</h3>

        <div className="space-y-1.5 mb-4 flex-1">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <CalendarDays size={12} className="text-gray-300 flex-shrink-0" />
            <span>
              {new Date(offer.startDate).toLocaleDateString()} →{' '}
              {new Date(offer.endDate).toLocaleDateString()}
            </span>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <TrendingUp size={12} className="text-emerald-400 flex-shrink-0" />
            <span>BV: ₹{(offer.businessVolumeRequired ?? offer.totalDirectInvestmentRequired).toLocaleString()}</span>
          </div>
          {offer.teamLevels?.length ? (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Users size={12} className="text-gray-300 flex-shrink-0" />
              <span>Levels: {offer.teamLevels.join(', ')}</span>
            </div>
          ) : null}
          {offer.selfInvestmentRequired > 0 && (
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <DollarSign size={12} className="text-blue-400 flex-shrink-0" />
              <span>Self: ₹{offer.selfInvestmentRequired.toLocaleString()}</span>
            </div>
          )}
          {offer.description ? (
            <p className="text-xs text-gray-400 line-clamp-2 mt-1">{offer.description}</p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-2">
          <button
            onClick={() => onEligible(offer)}
            className="flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#523680]/8 text-[#523680] text-xs font-semibold hover:bg-[#523680]/15 transition-colors"
          >
            <Users size={12} />
            Eligible Users
          </button>
          <button
            onClick={() => onProgress(offer)}
            className="flex items-center justify-center gap-1.5 py-2 rounded-xl bg-emerald-50 text-emerald-600 text-xs font-semibold hover:bg-emerald-100 transition-colors"
          >
            <BarChart2 size={12} />
            Progress
          </button>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onEdit(offer)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl border border-gray-200 text-xs font-semibold text-gray-500 hover:bg-gray-50 transition-colors"
          >
            <Pencil size={11} /> Edit
          </button>
          <button
            onClick={() => onDelete(offer._id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl border border-red-100 text-xs font-semibold text-red-400 hover:bg-red-50 transition-colors"
          >
            <Trash2 size={11} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const Offers: React.FC = () => {
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState<Offer | null>(null);
  const [eligibleOffer, setEligibleOffer] = useState<Offer | null>(null);
  const [progressOffer, setProgressOffer] = useState<Offer | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: AlertColor }>({
    open: false, message: '', severity: 'success',
  });

  const showSnack = (message: string, severity: AlertColor) =>
    setSnack({ open: true, message, severity });

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/offers');
      setOffers(data.data || []);
    } catch {
      showSnack('Failed to load offers', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOffers(); }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this offer? This cannot be undone.')) return;
    try {
      await api.delete(`/api/offers/${id}`);
      showSnack('Offer deleted', 'success');
      fetchOffers();
    } catch {
      showSnack('Failed to delete offer', 'error');
    }
  };

  if (progressOffer) {
    return (
      <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <ProgressPanel offer={progressOffer} onBack={() => setProgressOffer(null)} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-gray-900 uppercase tracking-tight">
              Offer Management
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Create and track investment offers with real-time progress
            </p>
          </div>
          <button
            onClick={() => { setEditingOffer(null); setFormOpen(true); }}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#523680] text-white text-sm font-bold hover:bg-[#523680]/90 transition-colors shadow-lg shadow-[#523680]/20"
          >
            <Plus size={16} /> New Offer
          </button>
        </div>

        {/* Offers grid */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <CircularProgress sx={{ color: '#523680' }} />
          </div>
        ) : offers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 bg-white rounded-3xl border border-gray-100 shadow-sm text-center">
            <div className="w-20 h-20 rounded-full bg-[#523680]/5 flex items-center justify-center mb-4">
              <Tag size={28} className="text-[#523680]/30" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">No Offers Yet</h3>
            <p className="text-gray-400 text-sm max-w-xs mt-1">
              Create your first investment offer to start tracking team progress.
            </p>
            <button
              onClick={() => { setEditingOffer(null); setFormOpen(true); }}
              className="mt-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#523680] text-white text-sm font-bold hover:bg-[#523680]/90 transition-colors"
            >
              <Plus size={14} /> Create First Offer
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {offers.map((offer) => (
              <OfferCard
                key={offer._id}
                offer={offer}
                onEdit={(o) => { setEditingOffer(o); setFormOpen(true); }}
                onDelete={handleDelete}
                onEligible={setEligibleOffer}
                onProgress={setProgressOffer}
              />
            ))}
          </div>
        )}
      </div>

      <OfferFormModal
        open={formOpen}
        editing={editingOffer}
        onClose={() => setFormOpen(false)}
        onSaved={fetchOffers}
        onSnack={showSnack}
      />

      <EligibleUsersModal
        open={!!eligibleOffer}
        offer={eligibleOffer}
        onClose={() => setEligibleOffer(null)}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 'bold' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Offers;
